import React from 'react';
import { Body1, Container, H5 } from '@ui';
import { TrackingEvent } from '@classes/TrackingEvent';
import { truncate } from '@utils/usefulFuncs';
import { submitQueriesStream } from '@services/genaiQueriesV2';
import { QuestionResult } from './components/QuestionResult';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import { ROUTES } from '@constants/routes';
import { AI_TOOLS, docTypes } from '@constants';
import { addAdvancedSettingValues, useAdvancedSettings } from '@hooks/application/useAdvancedSettings';
import { isEarningsFilename, logFormSubmit, logNetRequest } from '@utils/usefulFuncs';
import {
  DOC_TEMPLATE_AGENT_KEY_METRICS_PROMPT,
  DOC_TEMPLATE_AGENT_Q_AND_A_PROMPT,
  DOC_TEMPLATE_AGENT_SUMMARY_PROMPT,
} from '@containers/gridMode';
import styled from 'styled-components';
import { isNewsArticle } from '@utils/doc';

const filingPrompt =
  'Write an analyst report detailing revenue and earning metrics, major updates, and outlook in bullets.';
const updatePrompt = 'Write an analyst report detailing major updates in a few bullets.';
const newsPrompt = 'Write an analyst report detailing major updates in a few bullets.';
const docTypeToPrompt = {
  '6-K': updatePrompt,
  '8-K': updatePrompt,
  DEF14A: updatePrompt,
  DEFA14A: updatePrompt,
  'S-1': filingPrompt,
  '10-K': filingPrompt,
  '10-Q': filingPrompt,
  '20-F': filingPrompt,
  '40-F': filingPrompt,
};
const defaultPrompt = DOC_TEMPLATE_AGENT_SUMMARY_PROMPT;

const alternateDocTypePrompts = {
  [docTypes.EARNINGS]: DOC_TEMPLATE_AGENT_Q_AND_A_PROMPT,
  [docTypes.USER_EARNINGS]: DOC_TEMPLATE_AGENT_Q_AND_A_PROMPT,
};

const ActionsWrapper = styled.div`
  padding-top: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
`;

const tool = AI_TOOLS.DOC_SUMMARY;

const keyMetricsPrompt = DOC_TEMPLATE_AGENT_KEY_METRICS_PROMPT;

export function SummarizeDocument({ doc }) {
  const {
    id,
    doc_type: _internal_docType,
    filename,

    security_symbol: ticker,

    is_transcript_available: hasTranscript,
    document_url,
  } = doc;

  const docType = isEarningsFilename(filename) ? docTypes.USER_EARNINGS : _internal_docType;

  const advancedSettingsValues = useAdvancedSettings();
  const { model } = advancedSettingsValues;
  const [resultId, setResultId] = React.useState(null);
  const [alternateResultId, setAlternateResultId] = React.useState(null);
  const [keyMetricsResultId, setKeyMetricsResultId] = React.useState(null);

  const hasOriginalDoc = document_url && document_url.length > 0 ? document_url : false;
  const selectedDocs = [id];
  const tickers = [ticker];

  const askQuestion = async ({ prompt: question }) => {
    TrackingEvent.create('View Recent Document Summary', {
      Question: question,
      Tickers: tickers.length === 0 ? null : tickers,
      Model: model,
      'Selected Docs': selectedDocs,
    });

    const settings = { user_mode: tool };
    addAdvancedSettingValues(advancedSettingsValues, settings);
    logFormSubmit({ ...settings, question, doc_ids: selectedDocs, ticker: tickers });

    const result = await submitQueriesStream({
      questions: question,
      ticker: tickers.length === 0 ? null : tickers,
      docIds: selectedDocs,
      settings,
    });
    return result;
  };

  const onViewOriginalDoc = () => {
    if (hasOriginalDoc) {
      window.open(document_url, '_blank');
    } else {
      window.open(ROUTES.docViewer + `?id=${id}`, '_blank');
    }
  };

  const onViewTranscriptDoc = () => {
    window.open(ROUTES.docViewer + `?id=${id}`, '_blank');
  };

  const onQueryDocument = () => {
    const url = ROUTES.digitalAnalyst + `?doc-ids=${id}`;
    window.open(url, '_blank');
  };

  React.useEffect(() => {
    const getPromptResponse = async () => {
      const prompt = isNewsArticle(docType) ? newsPrompt : docTypeToPrompt[docType] || defaultPrompt;
      const { status, runId } = await askQuestion({ prompt });

      if (status === 200) {
        setResultId(runId);
      } else {
        setResultId(null);
      }
    };
    const getAlternatePromptResponse = async () => {
      const prompt = alternateDocTypePrompts[docType];
      if (!prompt) return;

      const { status: keyMetricsStatus, runId: keyMetricsRunId } = await askQuestion({ prompt: keyMetricsPrompt });

      if (keyMetricsStatus === 200) {
        setKeyMetricsResultId(keyMetricsRunId);
      } else {
        setKeyMetricsResultId(null);
      }

      const { status, runId } = await askQuestion({ prompt });

      if (status === 200) {
        setAlternateResultId(runId);
      } else {
        setAlternateResultId(null);
      }
    };

    if (model) {
      getPromptResponse();
      getAlternatePromptResponse();
    }
  }, [model]);

  if (docType === docTypes.EARNINGS || docType === docTypes.USER_EARNINGS) {
    return (
      <Container>
        {docType === docTypes.USER_EARNINGS ? <H5>{`${filename}`}</H5> : <H5>{`${ticker} - ${docType}`}</H5>}
        <ActionsWrapper>
          {hasTranscript && (
            <Container>
              <TransparentFlatButton onClick={onViewTranscriptDoc}>View Original Transcript</TransparentFlatButton>
            </Container>
          )}
          {hasOriginalDoc ? (
            docType === docTypes.USER_EARNINGS ? (
              <Container>
                <TransparentFlatButton onClick={onViewOriginalDoc}>View Original Document</TransparentFlatButton>
              </Container>
            ) : (
              <Container>
                <TransparentFlatButton onClick={onViewOriginalDoc}>Listen Original Document</TransparentFlatButton>
              </Container>
            )
          ) : null}
          <Container>
            <TransparentFlatButton onClick={onQueryDocument}>Query Document</TransparentFlatButton>
          </Container>
        </ActionsWrapper>
        <Container top={24}>
          <Body1 bold>Summary</Body1>
          <QuestionResult
            resultId={resultId}
            question={null}
            selectedDocs={selectedDocs}
            tickers={tickers}
            model={model}
            userMode={tool}
            enableMoreDetail
            collapseHeight
          />
        </Container>

        <Container top={24}>
          <Container bottom={10}>
            <Body1 bold>Key Metrics and Drivers</Body1>
          </Container>
          <QuestionResult
            resultId={keyMetricsResultId}
            question={null}
            selectedDocs={selectedDocs}
            tickers={tickers}
            model={model}
            userMode={tool}
            enableMoreDetail
            collapseHeight
          />
        </Container>

        <Container top={24}>
          <Body1 bold>Analyst Q&A</Body1>
          <QuestionResult
            resultId={alternateResultId}
            question={null}
            selectedDocs={selectedDocs}
            tickers={tickers}
            model={model}
            userMode={tool}
            enableMoreDetail
            collapseHeight
          />
        </Container>
      </Container>
    );
  }

  const docText = [
    docTypes.MELIUS_COMPANY_REPORT,
    docTypes.MELIUS_INDUSTRY_REPORT,
    docTypes.MELIUS_WEEKLY_VIDEO_SLIDES,
    docTypes.MELIUS_INDUSTRIAL_STRATEGY,
  ].includes(docType)
    ? `${docType} - ${filename}`
    : `${truncate(ticker, 30)} - ${docType}`;
  return (
    <Container>
      <Container>
        <H5>{docText}</H5>
      </Container>
      <Container>
        <QuestionResult
          resultId={resultId}
          question={null}
          selectedDocs={selectedDocs}
          tickers={tickers}
          model={model}
          userMode={tool}
          enableMoreDetail
          ActionsComponent={
            <ActionsWrapper>
              {hasTranscript && (
                <Container>
                  <FlatButton onClick={onViewTranscriptDoc}>View Original Transcript</FlatButton>
                </Container>
              )}
              {hasOriginalDoc && (
                <Container>
                  <TransparentFlatButton onClick={onViewOriginalDoc}>View Original Document</TransparentFlatButton>
                </Container>
              )}
              <Container>
                <TransparentFlatButton onClick={onQueryDocument}>Query Document</TransparentFlatButton>
              </Container>
            </ActionsWrapper>
          }
        />
      </Container>
    </Container>
  );
}
